import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
});

// Attach access_token from localStorage as Bearer fallback (mobile Safari samesite issues)
api.interceptors.request.use((cfg) => {
  const token = localStorage.getItem("nx_token");
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

// ---------------------------------------------------------------------------
// Access-control gate handler (Critical Task 3).
// The backend returns a structured body for two specific machine codes:
//   * `EMAIL_VERIFICATION_REQUIRED` — HTTP 403
//   * `ACTIVE_SUBSCRIPTION_REQUIRED` — HTTP 402
// Critical Task 4 adds a third:
//   * `PLAN_QUOTA_EXCEEDED` — HTTP 429
// Only these codes trigger a client-side redirect + Arabic/English toast.
// Any other 403 / 402 / 429 responses are passed through unchanged so callers
// can handle domain-specific denials (e.g. rate limiting) themselves.
// ---------------------------------------------------------------------------
const GATE_CODES = new Set([
  "EMAIL_VERIFICATION_REQUIRED",
  "ACTIVE_SUBSCRIPTION_REQUIRED",
  "PLAN_QUOTA_EXCEEDED",
  // DAAM Leads per-plan upgrade prompts (analytics range, active-campaign
  // cap, etc.). Same UX contract as ACTIVE_SUBSCRIPTION_REQUIRED: show
  // the AR/EN message + soft-redirect to /app/plans.
  "UPGRADE_REQUIRED",
]);

// Prevent redirect loops when we're already sitting on the destination.
function _alreadyOn(pathPrefix) {
  try {
    return window.location.pathname.startsWith(pathPrefix);
  } catch (_) {
    return false;
  }
}

// De-dupe toasts if a page fires multiple gated requests in parallel.
let _lastGateToastAt = 0;
function _showGateToast(detail) {
  const now = Date.now();
  if (now - _lastGateToastAt < 4000) return;
  _lastGateToastAt = now;
  const lang = (localStorage.getItem("nx_lang") || "ar").toLowerCase();
  const msg = lang === "ar"
    ? (detail?.message_ar || detail?.message_en || "")
    : (detail?.message_en || detail?.message_ar || "");
  if (msg) toast.error(msg);
}

function _handleGate(detail, cfg) {
  if (!detail || !GATE_CODES.has(detail.code)) return false;
  const defaultRoute = detail.code === "EMAIL_VERIFICATION_REQUIRED"
    ? "/verify-email"
    : "/app/plans"; // both ACTIVE_SUBSCRIPTION_REQUIRED and PLAN_QUOTA_EXCEEDED point at plans
  const target = detail.action?.route || defaultRoute;

  // DAAM Leads pages render their own on-page paywall banner, so we
  // want the toast but NOT the reflex redirect: bouncing the user off
  // /app/leads to /app/plans hides the very banner the leads page is
  // designed to show. The `EMAIL_VERIFICATION_REQUIRED` case still
  // redirects — that lives on a different route (/verify-email).
  const url = (cfg?.url || "");
  const isLeadsOwnerCall = url.includes("/leads/")
    && !url.includes("/leads/public/")
    && !url.includes("/leads/track/")
    && !url.includes("/leads/r/");
  if (isLeadsOwnerCall && detail.code !== "EMAIL_VERIFICATION_REQUIRED") {
    _showGateToast(detail);
    return true;   // handled: no default redirect, no retry
  }

  // Loop guard — do not navigate if the user is already on the target page.
  if (_alreadyOn(target)) {
    _showGateToast(detail);
    return true;
  }
  _showGateToast(detail);
  try {
    // Use setTimeout so the toast has time to mount before the route swap.
    setTimeout(() => { window.location.assign(target); }, 150);
  } catch (_) { /* no-op */ }
  return true;
}

// Auto-refresh on 401 (session still valid)
let _refreshInflight = null;
api.interceptors.response.use(
  (r) => r,
  async (err) => {
    const cfg = err.config;
    const status = err.response?.status;
    const detail = err.response?.data?.detail;

    // Access-control gate handling — ONLY for the specific documented codes.
    // (Fires before the 401 refresh path so gated responses don't get retried.)
    if ((status === 402 || status === 403 || status === 429) && detail && typeof detail === "object") {
      if (_handleGate(detail, cfg)) return Promise.reject(err);
    }

    const url = (cfg?.url || "").replace(API_BASE, "");
    const skip = url.startsWith("/auth/login") || url.startsWith("/auth/register")
      || url.startsWith("/auth/refresh") || url.startsWith("/auth/me")
      || url.startsWith("/auth/forgot-password") || url.startsWith("/auth/reset-password")
      || url.startsWith("/auth/verify-email") || url.startsWith("/demo/");
    if (status !== 401 || cfg._retried || skip) return Promise.reject(err);
    cfg._retried = true;
    try {
      if (!_refreshInflight) {
        _refreshInflight = axios.post(`${API_BASE}/auth/refresh`, {}, { withCredentials: true }).finally(() => { _refreshInflight = null; });
      }
      const { data } = await _refreshInflight;
      if (data?.access_token) localStorage.setItem("nx_token", data.access_token);
      cfg.headers.Authorization = `Bearer ${data.access_token}`;
      return api(cfg);
    } catch (e) {
      localStorage.removeItem("nx_token");
      return Promise.reject(err);
    }
  }
);

export function formatApiError(err) {
  const detail = err?.response?.data?.detail;
  if (!detail) return err?.message || "Something went wrong";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(", ");
  if (detail?.msg) return detail.msg;
  // Prefer localized messages when present (used by the access-control gate).
  const lang = (localStorage.getItem("nx_lang") || "ar").toLowerCase();
  if (lang === "ar" && detail?.message_ar) return detail.message_ar;
  if (detail?.message_en) return detail.message_en;
  if (detail?.message_ar) return detail.message_ar;
  return String(detail);
}
